import { Box, Button, Divider, Flex, Group, IconFont, Text } from '@byecode/ui'
import { ActionHandleEventType, type FilterFormType, type IndependentAction } from '@lighthouse/core'
import {
    actionTypeInfos,
    EndNodeType,
    flattenActionTypeInfos,
    FlowNode,
    getFlowIdByAction,
    ListItem4ByecodeUi,
    useAtomData
} from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { workflowListAtom } from '@/atoms/workflow/state'
import { AliyunMessageActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/AliyunMessageActionConfigure'
import { CallActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/CallActionConfigure'
import { CloseFloatActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/CloseFloatActionConfigure'
import { ControlActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/ControlActionConfigure'
import { CopyActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/CopyActionConfigure'
import { CreateSingleRecordActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/CreateSingleRecordActionConfigure'
import { DeleteRecordActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/DeleteRecordActionConfigure'
import { DownloadFileActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/DownloadFileActionConfigure'
import { NotificationActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/NotificationActionConfigure'
import { OpenLinkActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/OpenLinkActionConfigure'
import { OpenPageActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/OpenPageActionConfigure'
import { ScreenShotActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/ScreenShotActionConfigure'
import { ScrollToActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/ScrollToActionConfigure'
import { StripeActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/StripeActionConfigure'
import { UpdatePageShareActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/UpdatePageShareActionConfigure'
import { UpdatePageStateActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/UpdatePageStateActionConfigure'
import { UpdateQueryParameterActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/UpdateQueryParameterActionConfigure'
import { UpdateRecordActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/UpdateRecordActionConfigure'
import { OpenWeChatCustomerServiceActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/WeChatCustomerServiceActionConfigure'
import { WechatNavigateToMiniProgramActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/WechatNavigateToMiniProgramActionConfigure'
import { WechatOpenChannelsActivityActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/WechatOpenChannelsActivityActionConfigure'
import { WeChatPayActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/WeChatPayActionConfigure'
import { WxSubscriptionActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/WxSubscriptionActionConfigure'
import { WxSubscriptionNotificationActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/WxSubscriptionNotificationActionConfigure'
import { WxTemplateActionConfigure } from '@/containers/FlowDetail/components/NodeSetting/Actions/WxTemplateActionConfigure'
import { HandleClickConfigure } from '@/containers/FlowDetail/components/NodeSetting/Handle/HandleClickConfigure'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'

import { ConditionFilter } from '../ConditionFilter'
import { ActionTypeSelector } from './ActionTypeSelector'

const SCxHeader = styled(Flex)`
    flex-shrink: 0;
    height: 52px;
`

const SCxContent = styled(Box)`
    flex: 1;
    overflow: auto;
`

interface RecordConfigureProps {
    allParentActions: IndependentAction[]
    action: IndependentAction
    onClose: () => void
    onChange: (val: IndependentAction) => void
}

export const ActionAdderConfigure: React.FC<RecordConfigureProps> = ({ allParentActions, action, onClose, onChange }) => {
    const { id, type, data, config } = action
    const workflowList = useAtomData(workflowListAtom)
    const { viewType, viewDsId, formDsId, isImage } = useActionAdderDepParams()
    const actionTypeSwitcher = useMemo(
        () => (
            <ActionTypeSelector
                id={id}
                value={action}
                onChange={val => {
                    onChange(val)
                }}
            />
        ),
        [action, id, onChange]
    )

    const handleFilter = useCallback(
        (filter: FilterFormType) => {
            onChange({
                ...action,
                config: {
                    ...config,
                    executeFilter: filter
                }
            })
        },
        [action, config, onChange]
    )

    const handleChangeParams = useCallback(
        (nextValue: unknown) => {
            onChange({
                ...action,
                data: {
                    [type]: nextValue
                }
            } as IndependentAction)
        },
        [action, onChange, type]
    )

    const title = useMemo(() => {
        return flattenActionTypeInfos(actionTypeInfos).find(item => item.value === type)?.label
    }, [type])

    const actionNodeSettingContent = useMemo(() => {
        const commonProps = {
            actionTypeSwitcher,
            allParentActions
        }
        switch (type) {
            /** 行为节点 */
            case ActionHandleEventType.CREATE_RECORD: {
                return (
                    <CreateSingleRecordActionConfigure
                        {...commonProps}
                        value={data?.createRecord}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }
            case ActionHandleEventType.UPDATE_RECORD: {
                return <UpdateRecordActionConfigure {...commonProps} value={data?.updateRecord} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.DELETE_RECORD: {
                return <DeleteRecordActionConfigure {...commonProps} value={data?.deleteRecord} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.SCROLL_TO: {
                return <ScrollToActionConfigure {...commonProps} value={data?.scrollTo} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.CONTROL: {
                return <ControlActionConfigure {...commonProps} value={data?.control} onChange={val => handleChangeParams(val)} />
            }
            /** 动作流独有动作 */
            case ActionHandleEventType.COPY: {
                return <CopyActionConfigure {...commonProps} value={data?.copy} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.CALL: {
                return <CallActionConfigure {...commonProps} value={data?.call} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.OPEN_PAGE: {
                return <OpenPageActionConfigure {...commonProps} value={data?.openPage} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.OPEN_LINK: {
                return <OpenLinkActionConfigure {...commonProps} value={data?.openLink} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.NOTIFICATION: {
                return <NotificationActionConfigure {...commonProps} value={data?.notification} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.DOWNLOAD_FILE: {
                return <DownloadFileActionConfigure {...commonProps} value={data?.downloadFile} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.ALIYUN_MESSAGE: {
                return (
                    <AliyunMessageActionConfigure {...commonProps} value={data?.aliyunMessage} onChange={val => handleChangeParams(val)} />
                )
            }
            case ActionHandleEventType.WECHAT_PAY: {
                return <WeChatPayActionConfigure {...commonProps} value={data?.wechatPay} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.WECHAT_TEMPLATE_MSG: {
                return (
                    <WxTemplateActionConfigure {...commonProps} value={data?.wechatTemplateMsg} onChange={val => handleChangeParams(val)} />
                )
            }
            case ActionHandleEventType.STRIPE: {
                return <StripeActionConfigure {...commonProps} value={data?.stripe} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.CLOSE_FLOAT: {
                return <CloseFloatActionConfigure {...commonProps} />
            }
            case ActionHandleEventType.UPDATE_PAGE_STATE: {
                return (
                    <UpdatePageStateActionConfigure
                        {...commonProps}
                        value={data?.updatePageState}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }
            case ActionHandleEventType.UPDATE_PAGE_SHARE: {
                return (
                    <UpdatePageShareActionConfigure
                        {...commonProps}
                        value={data?.updatePageShare}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }
            case ActionHandleEventType.UPDATE_QUERY_PARAMETER: {
                return (
                    <UpdateQueryParameterActionConfigure
                        {...commonProps}
                        value={data?.updateQueryParameter}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }
            case ActionHandleEventType.SCREEN_SHOT: {
                return <ScreenShotActionConfigure {...commonProps} value={data?.screenShot} onChange={val => handleChangeParams(val)} />
            }
            case ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG: {
                return (
                    <WxSubscriptionActionConfigure
                        {...commonProps}
                        value={data?.wechatSubscriptionMsg}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }
            case ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION: {
                return (
                    <WxSubscriptionNotificationActionConfigure
                        {...commonProps}
                        value={data?.wechatSubscriptionMsgNotification}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }
            case ActionHandleEventType.OPEN_WECHAT_CUSTOMER_SERVICE: {
                return (
                    <OpenWeChatCustomerServiceActionConfigure
                        {...commonProps}
                        value={data?.openWechatCustomerService}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }
            case ActionHandleEventType.WECHAT_NAVIGATE_TO_MINI_PROGRAM: {
                return (
                    <WechatNavigateToMiniProgramActionConfigure
                        {...commonProps}
                        value={data?.wechatNavigateToMiniProgram}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }

            case ActionHandleEventType.WECHAT_OPEN_CHANNELS_ACTIVITY: {
                return (
                    <WechatOpenChannelsActivityActionConfigure
                        {...commonProps}
                        value={data?.wechatOpenChannelsActivity}
                        onChange={val => handleChangeParams(val)}
                    />
                )
            }

            case ActionHandleEventType.ACTION: {
                return <HandleClickConfigure {...commonProps} value={data.flow} onChange={val => handleChangeParams(val)} />
            }

            case ActionHandleEventType.AUTOMATION: {
                return <HandleClickConfigure {...commonProps} value={data.automation} onChange={val => handleChangeParams(val)} />
            }

            default: {
                return (
                    <Group label="动作配置" styles={{ root: { paddingTop: 2 } }}>
                        <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                            <div style={{ marginRight: 12 }}>类型</div>
                            {actionTypeSwitcher}
                        </ListItem4ByecodeUi>
                    </Group>
                )
            }
        }
    }, [actionTypeSwitcher, allParentActions, data, handleChangeParams, type])

    const upstreamNodes = useMemo(() => {
        return allParentActions.reduce<FlowNode[]>((acc, curr) => {
            if (curr.type === ActionHandleEventType.ACTION || curr.type === ActionHandleEventType.AUTOMATION) {
                const flowId = getFlowIdByAction(curr)
                const workflow = find(item => item.id === flowId, workflowList)
                if (!workflow) {
                    return acc
                }
                const endNode = find(item => item.data.nodeType === EndNodeType.END, workflow.content.nodes)
                if (!endNode) {
                    return acc
                }
                acc.push({
                    ...endNode,
                    id: curr.id
                })
            }
            return acc
        }, [])
    }, [allParentActions, workflowList])

    return (
        <Flex direction="column" style={{ flex: 1, overflow: 'hidden' }}>
            <SCxHeader px={16} justifyContent="space-between" alignItems="center">
                <Text weight={500} size={14}>
                    {title}
                </Text>
                <Button radius={100} type="tag" icon={<IconFont type="Close" size={16} />} onClick={onClose} />
            </SCxHeader>

            <Divider my={0} color="var(--color-gray-200)" />
            <SCxContent>
                <Box>{actionNodeSettingContent}</Box>

                <Divider my={0} color="var(--color-gray-200)" />

                <Box>
                    <Group label="执行条件">
                        <ConditionFilter
                            label="满足条件时执行"
                            isImage={isImage}
                            allParentActions={allParentActions}
                            workflowList={workflowList}
                            upstreamNodes={upstreamNodes}
                            formDsId={formDsId}
                            viewDsId={viewDsId}
                            viewType={viewType}
                            filter={config?.executeFilter}
                            onFilter={handleFilter}
                        />
                    </Group>
                </Box>
            </SCxContent>
        </Flex>
    )
}
